import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Input, Spin, Empty } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiClientesInstance, authHeaderClientes } from "../../redux/actions/clientes/clientes";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Eliminacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function ClienteDetallePage() {
  const { idCliente } = useParams();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [ventas, setVentas] = useState([]);
  const [encargos, setEncargos] = useState([]);
  const [loadingRel, setLoadingRel] = useState(false);

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchCliente = useCallback(() => {
    setLoading(true);
    apiClientesInstance.get(`/clientes/${idCliente}`, { headers: authHeaderClientes() })
      .then((res) => setCliente(res.data))
      .catch(() => toast.error("No se pudo cargar el cliente"))
      .finally(() => setLoading(false));
  }, [idCliente]);

  useEffect(() => { fetchCliente(); }, [fetchCliente]);

  useEffect(() => {
    if (!["ventas", "encargos"].includes(activeTab)) return;
    setLoadingRel(true);
    apiClientesInstance.get(`/clientes/${idCliente}/${activeTab}`, { headers: authHeaderClientes() })
      .then((res) => {
        if (activeTab === "ventas") setVentas(res.data || []);
        if (activeTab === "encargos") setEncargos(res.data || []);
      })
      .catch(() => {})
      .finally(() => setLoadingRel(false));
  }, [activeTab, idCliente]);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiClientesInstance.get(`/clientes/${idCliente}/actividad`, { headers: authHeaderClientes() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idCliente]);

  const startEditInfo = () => {
    setEditForm({
      nombre: cliente.nombre,
      telefono: cliente.telefono,
      rfc: cliente.rfc,
      razon_social: cliente.razon_social,
      codigo_postal_fiscal: cliente.codigo_postal_fiscal,
      regimen_fiscal: cliente.regimen_fiscal,
      uso_cfdi: cliente.uso_cfdi,
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!editForm.nombre?.trim()) return toast.warning("El nombre es obligatorio");
    setSavingInfo(true);
    try {
      await apiClientesInstance.patch(`/clientes/${idCliente}`, editForm, { headers: authHeaderClientes() });
      toast.success("Cliente actualizado correctamente");
      setEditingInfo(false);
      fetchCliente();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar este cliente?",
      content: "Esta accion desactiva el cliente. No se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiClientesInstance.delete(`/clientes/${idCliente}`, { headers: authHeaderClientes() });
          toast.success("Cliente eliminado");
          navigate("/app/clientes");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Cliente no encontrado" />
      </div>
    );
  }

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(cliente.nombre || "—").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> Creado el {fmtFechaCorta(cliente.datetime)}</span>
                {cliente.creado_por && <span className="cd-meta-item"><i className="fa-solid fa-user" /> {capitalize(cliente.creado_por)}</span>}
              </div>
            </div>
            <div className="cd-header-actions">
              <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "ventas" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("ventas")}>
              <i className="fa-solid fa-receipt" /> Ventas
            </button>
            <button className={`cd-tab-btn ${activeTab === "encargos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("encargos")}>
              <i className="fa-solid fa-box-archive" /> Encargos
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="cd-card-icon-wrap"><i className="fa-solid fa-address-book"></i></div>
                  <h2 className="cd-card-title">Informacion del cliente</h2>
                </div>
                {!editingInfo && (
                  <Button icon={<i className="fa-solid fa-pen" />} className="cd-btn-edit" onClick={startEditInfo}>Editar</Button>
                )}
              </div>

              {editingInfo ? (
                <div className="cd-edit-form">
                  <div className="cd-edit-grid">
                    <div className="cd-edit-field"><span className="cd-edit-label">Nombre</span><Input value={editForm.nombre} onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} /></div>
                    <div className="cd-edit-field"><span className="cd-edit-label">Telefono</span><Input value={editForm.telefono || ""} onChange={(e) => setEditForm((f) => ({ ...f, telefono: e.target.value }))} /></div>
                    <div className="cd-edit-field"><span className="cd-edit-label">Razon social</span><Input value={editForm.razon_social || ""} onChange={(e) => setEditForm((f) => ({ ...f, razon_social: e.target.value }))} /></div>
                    <div className="cd-edit-field"><span className="cd-edit-label">RFC</span><Input value={editForm.rfc || ""} onChange={(e) => setEditForm((f) => ({ ...f, rfc: e.target.value }))} /></div>
                    <div className="cd-edit-field"><span className="cd-edit-label">Codigo postal fiscal</span><Input value={editForm.codigo_postal_fiscal || ""} onChange={(e) => setEditForm((f) => ({ ...f, codigo_postal_fiscal: e.target.value }))} /></div>
                    <div className="cd-edit-field"><span className="cd-edit-label">Regimen fiscal</span><Input value={editForm.regimen_fiscal || ""} onChange={(e) => setEditForm((f) => ({ ...f, regimen_fiscal: e.target.value }))} /></div>
                    <div className="cd-edit-field"><span className="cd-edit-label">Uso de CFDI</span><Input value={editForm.uso_cfdi || ""} onChange={(e) => setEditForm((f) => ({ ...f, uso_cfdi: e.target.value }))} /></div>
                  </div>
                  <div className="cd-edit-actions">
                    <Button icon={<CloseOutlined />} onClick={() => setEditingInfo(false)} disabled={savingInfo}>Cancelar</Button>
                    <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Guardar</Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                  <div><span className="cd-field-label">Nombre</span><span className="cd-field-value">{capitalize(cliente.nombre)}</span></div>
                  <div><span className="cd-field-label">Telefono</span><span className="cd-field-value">{cliente.telefono || "—"}</span></div>
                  <div><span className="cd-field-label">Razon social</span><span className="cd-field-value">{cliente.razon_social || "—"}</span></div>
                  <div><span className="cd-field-label">RFC</span><span className="cd-field-value">{cliente.rfc || "—"}</span></div>
                  <div><span className="cd-field-label">Codigo postal fiscal</span><span className="cd-field-value">{cliente.codigo_postal_fiscal || "—"}</span></div>
                  <div><span className="cd-field-label">Regimen fiscal</span><span className="cd-field-value">{cliente.regimen_fiscal || "—"}</span></div>
                  <div><span className="cd-field-label">Uso de CFDI</span><span className="cd-field-value">{cliente.uso_cfdi || "—"}</span></div>
                  <div><span className="cd-field-label">Fecha de creacion</span><span className="cd-field-value">{fmtFechaCorta(cliente.datetime)}</span></div>
                </div>
              )}
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-coins"></i></div>
                <h2 className="cd-card-title">Saldo</h2>
              </div>
              <div>
                <span className="cd-field-label">Saldo a favor</span>
                <span className="cd-field-value" style={{ fontSize: 20 }}>{money(cliente.saldo_a_favor)}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === "ventas" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-receipt"></i></div>
              <h2 className="cd-card-title">Ventas de este cliente</h2>
            </div>
            {loadingRel ? <Spin /> : ventas.length === 0 ? (
              <Empty description="Todavia no hay ventas registradas para este cliente" />
            ) : (
              <div className="cd-pagos-list">
                {ventas.map((v) => (
                  <div key={v.id_venta} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Total</span><div className="cd-field-value">{money(v.total)}</div></div>
                    <div><span className="cd-field-label">Saldo pendiente</span><div className="cd-field-value">{money(v.saldo_pendiente)}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(v.datetime)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "encargos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-box-archive"></i></div>
              <h2 className="cd-card-title">Encargos de este cliente</h2>
            </div>
            {loadingRel ? <Spin /> : encargos.length === 0 ? (
              <Empty description="Todavia no hay encargos registrados para este cliente" />
            ) : (
              <div className="cd-pagos-list">
                {encargos.map((e) => (
                  <div key={e.id_encargo} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Descripcion</span><div className="cd-field-value">{e.descripcion}</div></div>
                    <div><span className="cd-field-label">Estado</span><div className="cd-field-value">{e.estado_nombre || "—"}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(e.datetime)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
